import { fetchAPI, METHODS, parseBody } from './common';
import { waitms } from 'src/utils/wait';
import * as Sentry from '@sentry/react';

const UPLOAD_STALL_TIMEOUT_MS = 60 * 1000;
const UPLOAD_ATTEMPTS = 2;
const UPLOAD_RETRY_DELAY_MS = 1000;
const MAX_CONCURRENT_UPLOADS = 3;

class UploadError extends Error {
  constructor(file, { attempts = 0, status, statusText, body, reason } = {}) {
    super(
      `Couldn't upload ${file?.name ? `"${file.name}"` : 'a file'}. ` +
        'Check your connection and try again.'
    );
    this.name = 'UploadError';
    this.attempts = attempts;
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.reason = reason;
  }
}

const logErrorInSentry = ({ file, path, source, err }) => {
  Sentry.withScope((scope) => {
    scope.setContext('file_metadata', {
      source,
      size: file?.size,
      path: file?.path,
      destPath: path,
      lastModifiedDate: file?.lastModifiedDate,
      lastModified: file?.lastModified,
      status: err?.status,
      statusText: err?.statusText,
      s3ErrorCode: err?.body?.match(/<Code>([^<]+)<\/Code>/)?.[1],
      responseBody: err?.body?.slice(0, 1000),
      reason: err?.reason,
      attempts: err?.attempts,
      fileName: file?.name,
      fileType: file?.type,
      message: err?.message,
      href: window.location?.href,
    });
    scope.captureException(new Error('Error while uploading file to s3'));
  });
};

let activeUploads = 0;
const queuedUploads = [];

async function withUploadSlot(task) {
  if (activeUploads < MAX_CONCURRENT_UPLOADS) {
    activeUploads++;
  } else {
    await new Promise((resolve) => queuedUploads.push(resolve));
  }
  try {
    return await task();
  } finally {
    const next = queuedUploads.shift();
    if (next) {
      next();
    } else {
      activeUploads--;
    }
  }
}

function putOnce(url, body) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let stallTimer;
    let stalled = false;

    const armStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, UPLOAD_STALL_TIMEOUT_MS);
    };
    const settle = (result) => {
      clearTimeout(stallTimer);
      resolve(result);
    };

    xhr.upload.addEventListener('progress', armStallTimer);
    xhr.addEventListener('load', () =>
      settle({
        status: xhr.status,
        statusText: xhr.statusText,
        body: xhr.responseText,
      })
    );
    xhr.addEventListener('error', () => settle({ reason: 'network' }));
    xhr.addEventListener('abort', () =>
      settle({ reason: stalled ? 'stalled' : 'aborted' })
    );

    xhr.open('PUT', url);
    armStallTimer();
    xhr.send(body);
  });
}

const isRetryable = ({ status }) => !status || status === 400 || status >= 500;

async function putWithRetry(url, body, file) {
  let result;
  let attempts = 0;
  while (attempts < UPLOAD_ATTEMPTS) {
    if (attempts > 0) {
      await waitms(UPLOAD_RETRY_DELAY_MS);
    }
    attempts++;
    result = await putOnce(url, body);
    if (result.status >= 200 && result.status < 300) {
      return;
    }
    if (!isRetryable(result)) {
      break;
    }
  }
  throw new UploadError(file, { ...result, attempts });
}

/**
 * @typedef UploadConfig
 * @property {string} folder
 * @property {string} filetype

/**
 * Uploads an image to an S3 bucket, returns it's url.
 * @param {File} file
 * @param {string} data This is a data URI from a FileReader
 * @param {UploadConfig} config
 */
export async function upload(file, data, config, path = 'donation', source) {
  return withUploadSlot(async () => {
    try {
      // get signed url
      const res = await fetchAPI('/image/signed-url', METHODS.post, config);
      let { url, fileName } = await parseBody(res);
      if (!url) {
        throw new UploadError(file);
      }

      // upload image
      let blobData = null;
      if (typeof data === 'string') {
        // use fetch to convert datauri to blob data
        const response = await fetch(data);
        blobData = await response.blob();
      } else {
        // convert array buffer to blob
        blobData = new Blob([data], { type: file.type });
      }
      await putWithRetry(url, blobData, file);

      return `${path}/${fileName}`;
    } catch (err) {
      logErrorInSentry({ file, path, source, err });
      throw err;
    }
  });
}

export async function copy({ folder, url, stock = false }) {
  const res = await fetchAPI('/image/copy-image', METHODS.post, {
    folder,
    url,
    stock,
  });
  const { fileName } = await parseBody(res);
  return `${folder}/${fileName}`;
}

/**
 * Request a short-lived presigned URL for downloading an S3 object.
 * Accepts either a raw S3 key or a full S3 URL.
 * The backend sets Content-Disposition for proper filename on download.
 */
export async function getDownloadUrl({ key, url, filename, expiresIn } = {}) {
  if ((!key || key === '') && (!url || url === '')) {
    throw new Error('Either key or url must be provided to getDownloadUrl');
  }
  const res = await fetchAPI('/image/download-url', METHODS.post, {
    key,
    url,
    filename,
    expiresIn,
  });
  return parseBody(res, true);
}
